import type { CreateAgentInput } from './api/types';

export interface AgentTemplate {
  id: string;
  name: string;
  capability: string;
  summary: string;
  defaults: Pick<CreateAgentInput, 'role' | 'goal' | 'instructions' | 'modelPolicy' | 'approvalPolicy'>;
}

const governedModel = {
  strategy: 'capability',
  capabilityProfile: 'insurance-reasoning',
  allowedProviders: ['azure-openai'],
  temperature: 0.2,
  structuredOutputRequired: true,
};

export const AGENT_TEMPLATES: AgentTemplate[] = [
  {
    id: 'submission-intake',
    name: 'Submission Intake Agent',
    capability: 'Submission intake',
    summary: 'Collects and structures a submission. It does not decide straight-through processing.',
    defaults: {
      role: 'Submission intake specialist',
      goal: 'Prepare a complete submission for assessment.',
      instructions: 'Normalize the submission facts and missing items. Do not underwrite, price, or bind cover.',
      modelPolicy: { ...governedModel, capabilityProfile: 'submission-intake' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'kyc-compliance',
    name: 'KYC / Compliance Agent',
    capability: 'KYC and compliance',
    summary: 'Returns a screening result for the journey. It does not override a failed check.',
    defaults: {
      role: 'Compliance screening specialist',
      goal: 'Record the KYC and compliance result for the case.',
      instructions: 'Return the screening result supplied by the governed check. Never convert FAIL to PASS.',
      modelPolicy: { ...governedModel, capabilityProfile: 'kyc-screening' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'underwriting-assist',
    name: 'Underwriting Assist Agent',
    capability: 'Underwriting assist',
    summary: 'Explains an automated assessment for a human underwriter. It does not make the decision.',
    defaults: {
      role: 'Underwriting assistant',
      goal: 'Explain the referral so a human underwriter can decide.',
      instructions: 'Summarize the case and the automated assessment. Do not approve, reject, or bind cover.',
      modelPolicy: { ...governedModel, capabilityProfile: 'underwriting-assist' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'pricing-assist',
    name: 'Pricing Assist Agent',
    capability: 'Pricing assist',
    summary: 'Explains pricing inputs for a governed rating step.',
    defaults: {
      role: 'Pricing assistant',
      goal: 'Explain pricing inputs without issuing a rate.',
      instructions: 'Describe the rating inputs and deviations. Do not publish a premium or bind cover.',
      modelPolicy: { ...governedModel, capabilityProfile: 'pricing-assist' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'claims-triage',
    name: 'Claims Triage Agent',
    capability: 'Claims triage',
    summary: 'Classifies a claim for the next human or system step.',
    defaults: {
      role: 'Claims triage specialist',
      goal: 'Classify the claim and the next action.',
      instructions: 'Triage the claim facts. Do not settle or deny the claim.',
      modelPolicy: { ...governedModel, capabilityProfile: 'claims-triage' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'reinsurance-assist',
    name: 'Reinsurance Assist Agent',
    capability: 'Reinsurance assist',
    summary: 'Prepares a reinsurance referral summary.',
    defaults: {
      role: 'Reinsurance assistant',
      goal: 'Summarize a placement for a reinsurance specialist.',
      instructions: 'Prepare the placement summary. Do not bind a treaty or facultative placement.',
      modelPolicy: { ...governedModel, capabilityProfile: 'reinsurance-assist' },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
  {
    id: 'blank',
    name: 'Blank Governed Agent',
    capability: 'Governed agent',
    summary: 'Starts from an empty governed contract. Role, goal, and instructions are required.',
    defaults: {
      role: '',
      goal: '',
      instructions: '',
      modelPolicy: { ...governedModel },
      approvalPolicy: { materialActions: 'HUMAN' },
    },
  },
];

export function templateById(id: string) {
  return AGENT_TEMPLATES.find(template => template.id === id);
}

export function emptyCreateInput(templateId = 'blank'): CreateAgentInput {
  const template = templateById(templateId) ?? AGENT_TEMPLATES[AGENT_TEMPLATES.length - 1];
  return {
    templateId: template.id,
    agentKey: '',
    name: template.id === 'blank' ? '' : template.name,
    description: template.summary,
    owner: '',
    capability: template.capability,
    versionLabel: '0.1.0',
    role: template.defaults.role,
    goal: template.defaults.goal,
    instructions: template.defaults.instructions,
    modelPolicy: { ...template.defaults.modelPolicy },
    memoryPolicy: { enabled: false, scope: 'execution', piiAllowed: false },
    permissionPolicy: { environments: ['DEV'], privilegedActionsAllowed: false },
    approvalPolicy: { ...template.defaults.approvalPolicy },
    outputSchema: { type: 'object' },
    evaluationPolicy: { required: true },
    toolBindings: [],
    knowledgeBindings: [],
  };
}
